import React, { useState, useEffect } from 'react';
import { apiService } from '../../services/apiService';
import { motion } from 'framer-motion';
import { User, Lock, Mail, Phone, Building } from 'lucide-react';
import Card from '../../components/Card';
import Button from '../../components/Button';
import Input from '../../components/Input';
import { useAuth } from '../../contexts/AuthContext';
import { authService } from '../../services/authService';
import Cookies from 'js-cookie';

const ClientProfile = () => {
  const { user, updateUser } = useAuth();
  const [activeTab, setActiveTab] = useState('profile');
  const [profileData, setProfileData] = useState({
    name: user?.name || '',
    email: user?.email || '',
    phone: user?.phone || '',
    company: user?.company || '',
    address: '',
    city: '',
    postalCode: '',
  });
  const [passwordData, setPasswordData] = useState({
    oldPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [accountMeta, setAccountMeta] = useState({ createdAt: null, lastLogin: null });

  useEffect(() => {
    const load = async () => {
      if (!user?.id) return;
      try {
        const client = await apiService.getClient(user.id);
        setProfileData({
          name: client.name || user.name || '',
          email: client.email || user.email || '',
          phone: client.phone || '',
          company: client.company || user.company || '',
          address: client.address || '',
          city: client.city || '',
          postalCode: client.postalCode || client.postal_code || '',
        });
        setAccountMeta({
          createdAt: client.createdAt || client.created_at || null,
          lastLogin: client.lastLogin || client.last_login || null,
        });
      } catch {
        /* keep session defaults */
      }
    };
    load();
  }, [user?.id, user?.name, user?.email, user?.company]);

  const handleProfileChange = (e) => {
    setProfileData({ ...profileData, [e.target.name]: e.target.value });
  };

  const handlePasswordChange = (e) => {
    setPasswordData({ ...passwordData, [e.target.name]: e.target.value });
  };

  const handleProfileSubmit = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);
      await apiService.updateClient(user.id, {
        name: profileData.name,
        company: profileData.company,
        phone: profileData.phone,
        address: profileData.address,
        city: profileData.city,
        postalCode: profileData.postalCode,
      });
      updateUser({
        ...user,
        name: profileData.name,
        company: profileData.company,
        phone: profileData.phone,
      });
      setMessage({ type: 'success', text: 'Profil mis à jour avec succès' });
    } catch (error) {
      setMessage({ type: 'error', text: error.message || 'Erreur lors de la mise à jour' });
    } finally {
      setSaving(false);
    }
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setMessage({ type: 'error', text: 'Les mots de passe ne correspondent pas' });
      return;
    }

    try {
      setSaving(true);
      const token = Cookies.get('petrofeu_token');
      await authService.updatePassword(
        user.id,
        passwordData.oldPassword,
        passwordData.newPassword,
        token
      );
      setMessage({ type: 'success', text: 'Mot de passe mis à jour avec succès' });
      setPasswordData({ oldPassword: '', newPassword: '', confirmPassword: '' });
    } catch (error) {
      setMessage({ type: 'error', text: 'Erreur lors de la mise à jour du mot de passe' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Mon Profil</h1>
        <p className="text-gray-600 mt-1">Gérez vos informations personnelles</p>
      </div>

      {/* Tabs */}
      <Card>
        <div className="flex space-x-4 border-b border-gray-200">
          <button
            onClick={() => setActiveTab('profile')}
            className={`px-4 py-2 font-medium transition-colors border-b-2 ${
              activeTab === 'profile'
                ? 'text-primary-600 border-primary-600'
                : 'text-gray-600 border-transparent hover:text-gray-900'
            }`}
          >
            Informations personnelles
          </button>
          <button
            onClick={() => setActiveTab('password')}
            className={`px-4 py-2 font-medium transition-colors border-b-2 ${
              activeTab === 'password'
                ? 'text-primary-600 border-primary-600'
                : 'text-gray-600 border-transparent hover:text-gray-900'
            }`}
          >
            Mot de passe
          </button>
        </div>
      </Card>

      {/* Message */}
      {message.text && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className={`p-4 rounded-lg ${
            message.type === 'success'
              ? 'bg-green-50 border border-green-200 text-green-700'
              : 'bg-red-50 border border-red-200 text-red-700'
          }`}
        >
          {message.text}
        </motion.div>
      )}

      {/* Profile Tab */}
      {activeTab === 'profile' && (
        <Card>
          <form onSubmit={handleProfileSubmit} className="space-y-6">
            <div className="flex items-center space-x-6">
              <div className="w-24 h-24 rounded-full bg-gradient-primary flex items-center justify-center text-white text-3xl font-bold">
                {user?.name?.charAt(0)?.toUpperCase()}
              </div>
              <div>
                <h3 className="text-xl font-semibold text-gray-900">{user?.name}</h3>
                <p className="text-gray-600">{user?.company}</p>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-2 sm:gap-4">
              <Input
                label="Nom complet"
                name="name"
                value={profileData.name}
                onChange={handleProfileChange}
                icon={User}
              />
              <Input
                label="Entreprise"
                name="company"
                value={profileData.company}
                onChange={handleProfileChange}
                icon={Building}
              />
              <Input
                label="Email (connexion)"
                type="email"
                name="email"
                value={profileData.email}
                onChange={handleProfileChange}
                icon={Mail}
                disabled
              />
              <Input
                label="Téléphone"
                name="phone"
                value={profileData.phone}
                onChange={handleProfileChange}
                icon={Phone}
              />
              <Input
                label="Adresse"
                name="address"
                value={profileData.address}
                onChange={handleProfileChange}
                className="sm:col-span-2"
              />
              <Input
                label="Ville"
                name="city"
                value={profileData.city}
                onChange={handleProfileChange}
              />
              <Input
                label="Code postal"
                name="postalCode"
                value={profileData.postalCode}
                onChange={handleProfileChange}
              />
            </div>

            <div className="flex justify-end">
              <Button type="submit" variant="primary" loading={saving}>
                Enregistrer les modifications
              </Button>
            </div>
          </form>
        </Card>
      )}

      {/* Password Tab */}
      {activeTab === 'password' && (
        <Card>
          <form onSubmit={handlePasswordSubmit} className="space-y-6">
            <Input
              label="Mot de passe actuel"
              type="password"
              name="oldPassword"
              value={passwordData.oldPassword}
              onChange={handlePasswordChange}
              icon={Lock}
              placeholder="Saisissez votre mot de passe actuel"
              required
            />
            <Input
              label="Nouveau mot de passe"
              type="password"
              name="newPassword"
              value={passwordData.newPassword}
              onChange={handlePasswordChange}
              icon={Lock}
              placeholder="Nouveau mot de passe (min. 8 caractères)"
              required
            />
            <Input
              label="Confirmer le nouveau mot de passe"
              type="password"
              name="confirmPassword"
              value={passwordData.confirmPassword}
              onChange={handlePasswordChange}
              icon={Lock}
              placeholder="Confirmez le nouveau mot de passe"
              required
            />

            <div className="flex justify-end">
              <Button type="submit" variant="primary" loading={saving}>
                Changer le mot de passe
              </Button>
            </div>
          </form>
        </Card>
      )}

      {/* Account Info */}
      <Card>
        <h2 className="text-xl font-semibold text-gray-900 mb-4">Informations du compte</h2>
        <div className="space-y-3">
          <div className="flex items-center justify-between py-2 border-b border-gray-200">
            <span className="text-gray-600">Type de compte</span>
            <span className="font-medium text-gray-900">Client</span>
          </div>
          <div className="flex items-center justify-between py-2 border-b border-gray-200">
            <span className="text-gray-600">Membre depuis</span>
            <span className="font-medium text-gray-900">
              {accountMeta.createdAt
                ? new Date(accountMeta.createdAt).toLocaleDateString('fr-FR')
                : '-'}
            </span>
          </div>
          <div className="flex items-center justify-between py-2">
            <span className="text-gray-600">Dernière connexion</span>
            <span className="font-medium text-gray-900">
              {accountMeta.lastLogin
                ? new Date(accountMeta.lastLogin).toLocaleDateString('fr-FR')
                : '-'}
            </span>
          </div>
        </div>
      </Card>
    </div>
  );
};

export default ClientProfile;

